import React, { useState, useEffect } from 'react';
import { Search, Filter, X, User } from 'lucide-react';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';

export default function RoleSearch({ onRoleSelect, selectedRole }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [voiceTypeFilter, setVoiceTypeFilter] = useState('');
  const [genreFilter, setGenreFilter] = useState('');

  const voiceTypes = ['soprano', 'mezzo_soprano', 'alto', 'tenor', 'baritone', 'bass', 'speaking'];
  const genres = ['broadway', 'off_broadway', 'community', 'opera', 'operetta', 'rock', 'pop'];

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!searchQuery?.trim() && !voiceTypeFilter && !genreFilter) {
      setError('Please enter a search term or apply filters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Dynamic import to avoid bundling issues
      const { searchRoles } = await import('../../../services/auditionSongService');
      
      const filters = {};
      if (voiceTypeFilter) filters.voiceType = voiceTypeFilter;
      if (genreFilter) filters.genre = genreFilter;

      const results = await searchRoles(searchQuery, filters);
      setSearchResults(results || []);

      if (results?.length === 0) {
        setError('No roles found matching your search. Try different keywords or filters.');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err?.message || 'Failed to search roles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setVoiceTypeFilter('');
    setGenreFilter('');
  };

  const hasActiveFilters = voiceTypeFilter || genreFilter;

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">Search for a Role</h2>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            hasActiveFilters ? 'bg-purple-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
          }`}
        >
          <Filter className="w-4 h-4" />
          <span className="text-sm">Filters</span>
        </button>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            placeholder="Search by role name or musical title..."
            className="w-full bg-white/5 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
          />
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white/5 rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium">Filters</h3>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm text-purple-300 hover:text-purple-200 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
            </div>

            {/* Voice Type Filter */}
            <div>
              <label className="block text-white/70 text-sm mb-2">Voice Type</label>
              <select
                value={voiceTypeFilter}
                onChange={(e) => setVoiceTypeFilter(e?.target?.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-400"
              >
                <option value="" className="bg-gray-900">All Voice Types</option>
                {voiceTypes?.map(type => (
                  <option key={type} value={type} className="bg-gray-900">
                    {type?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* Genre Filter */}
            <div>
              <label className="block text-white/70 text-sm mb-2">Musical Genre</label>
              <select
                value={genreFilter}
                onChange={(e) => setGenreFilter(e?.target?.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-400"
              >
                <option value="" className="bg-gray-900">All Genres</option>
                {genres?.map(genre => (
                  <option key={genre} value={genre} className="bg-gray-900">
                    {genre?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Searching...' : 'Search Roles'}
        </button>
      </form>

      {/* Error Message */}
      {error && (
        <div className="mt-4 bg-red-500/10 border border-red-500 rounded-xl p-3">
          <p className="text-red-200 text-sm">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mt-6 flex items-center justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {/* Search Results */}
      {!loading && searchResults?.length > 0 && (
        <div className="mt-6 space-y-3">
          <h3 className="text-white font-semibold">
            {searchResults?.length} role{searchResults?.length !== 1 ? 's' : ''} found
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {searchResults?.map((role) => (
              <div
                key={role?.roleId}
                onClick={() => onRoleSelect(role)}
                className={`flex gap-4 p-4 rounded-xl cursor-pointer transition-all ${
                  selectedRole?.roleId === role?.roleId
                    ? 'bg-purple-600/40 border-2 border-purple-400' :'bg-white/5 hover:bg-white/10 border-2 border-transparent'
                }`}
              >
                <img
                  src={role?.musicalPoster || '/assets/images/no_image.png'}
                  alt={role?.musicalTitle}
                  className="w-16 h-24 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h4 className="text-white font-medium">{role?.characterName}</h4>
                  <p className="text-purple-300 text-sm">{role?.musicalTitle}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
                      {role?.voiceType?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </span>
                    {role?.vocalRange && (
                      <span className="px-2 py-1 bg-blue-500/30 text-blue-200 rounded text-xs">
                        {role?.vocalRange}
                      </span>
                    )}
                    {role?.roleType && (
                      <span className="px-2 py-1 bg-pink-500/30 text-pink-200 rounded text-xs">
                        {role?.roleType?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                      </span>
                    )}
                  </div>
                  {role?.description && (
                    <p className="text-white/70 text-sm mt-2 line-clamp-2">{role?.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Role Display */}
      {selectedRole && (
        <div className="mt-6 bg-gradient-to-r from-purple-600/30 to-pink-600/30 rounded-xl p-4 border border-purple-400/30">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-purple-300" />
            <h3 className="text-white font-semibold">Selected Role</h3>
          </div>
          <div className="flex gap-3">
            <img
              src={selectedRole?.musicalPoster || '/assets/images/no_image.png'}
              alt={selectedRole?.musicalTitle}
              className="w-12 h-18 object-cover rounded-lg"
            />
            <div className="flex-1">
              <p className="text-white font-medium">{selectedRole?.characterName}</p>
              <p className="text-purple-200 text-sm">{selectedRole?.musicalTitle}</p>
              <span className="inline-block mt-1 px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
                {selectedRole?.voiceType?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}